import {
  IonBackButton,
  IonButton,
  IonButtons,
  IonCheckbox,
  IonContent,
  IonHeader,
  IonIcon,
  IonInput,
  IonItem,
  IonLabel,
  IonList,
  IonPage,
  IonSelect,
  IonSelectOption,
  IonSpinner,
  IonTextarea,
  IonTitle,
  IonToolbar,
  useIonToast,
} from "@ionic/react";
import { useEffect, useRef, useState } from "react";
import {
  alertCircleOutline,
  arrowBackOutline,
  cameraOutline,
  checkmarkCircleOutline,
  createOutline,
  searchOutline,
} from "ionicons/icons";
import { Html5Qrcode } from "html5-qrcode";

import {
  buscarFuncionarios,
  entregarManual,
  escanearQR,
  FuncionarioActivo,
  MiTemaItem,
  obtenerMiSaldo,
  obtenerMisTemas,
  obtenerStockBuckets,
  StockBucket,
  Tema,
} from "src/services/entregasMasivas";

type Modo = "menu" | "qr" | "manual";
type EstadoQR = "iniciando" | "escaneando" | "procesando" | "ok" | "error";
type EstadoManual = "buscar" | "motivo" | "enviando" | "ok" | "error";

interface UltimoEscaneo {
  receptor?: string;
  mensaje?: string;
  detalle?: string;
}

const SCANNER_ID = "qr-scanner-region";

const bucketLabel = (b: StockBucket): string => {
  const cat = b.categoria || "General";
  return b.identificador ? `${cat} - N° ${b.identificador}` : cat;
};

const obtenerUbicacion = (): Promise<{
  lat: number | null;
  lng: number | null;
  precision: number | null;
}> => {
  return new Promise((resolve) => {
    if (!navigator.geolocation) {
      resolve({ lat: null, lng: null, precision: null });
      return;
    }
    navigator.geolocation.getCurrentPosition(
      (pos) =>
        resolve({
          lat: pos.coords.latitude,
          lng: pos.coords.longitude,
          precision: pos.coords.accuracy ?? null,
        }),
      () => resolve({ lat: null, lng: null, precision: null }),
      { enableHighAccuracy: true, timeout: 5000, maximumAge: 0 }
    );
  });
};

const RegistrarEntrega: React.FC = () => {
  const [modo, setModo] = useState<Modo>("menu");

  // Tema (puede haber varios vigentes; el entregador elige cual operar).
  const [temas, setTemas] = useState<MiTemaItem[]>([]);
  const [tema, setTema] = useState<Tema | null>(null);
  const [cargandoTema, setCargandoTema] = useState(true);

  // Saldo del entregador (descuenta con cada entrega).
  const [saldo, setSaldo] = useState<number | null>(null);

  // Items disponibles del entregador (cuando el tema controla categorias).
  // En el registro manual se pueden buscar y seleccionar varios (cada item
  // serializado = una entrega). bucketsSel son los items elegidos.
  const [buckets, setBuckets] = useState<StockBucket[]>([]);
  const [bucketsSel, setBucketsSel] = useState<StockBucket[]>([]);
  const [bucketSearch, setBucketSearch] = useState("");

  // QR
  const [estadoQR, setEstadoQR] = useState<EstadoQR>("iniciando");
  const [errorQR, setErrorQR] = useState<string>("");
  const [ultimo, setUltimo] = useState<UltimoEscaneo | null>(null);

  // Manual
  const [busqueda, setBusqueda] = useState("");
  const [resultados, setResultados] = useState<FuncionarioActivo[]>([]);
  const [buscando, setBuscando] = useState(false);
  const [seleccionado, setSeleccionado] = useState<FuncionarioActivo | null>(null);
  const [motivo, setMotivo] = useState("");
  const [estadoManual, setEstadoManual] = useState<EstadoManual>("buscar");
  const [resultadoManual, setResultadoManual] = useState<UltimoEscaneo | null>(null);

  const [present] = useIonToast();

  const scannerRef = useRef<Html5Qrcode | null>(null);
  const procesandoRef = useRef<boolean>(false);
  const ultimoQRRef = useRef<string>("");
  const ultimoQRTsRef = useRef<number>(0);

  // ---------------------------------------------------------------------------
  // Tema
  // ---------------------------------------------------------------------------
  const refrescarSaldo = async (temaId?: number) => {
    try {
      const s = await obtenerMiSaldo(temaId);
      setSaldo(s?.saldo ?? null);
    } catch {
      /* silencioso: el saldo es informativo */
    }
  };

  const cargarBuckets = async (t: Tema | null) => {
    if (!t || !t.controla_categorias) {
      setBuckets([]);
      setBucketsSel([]);
      return;
    }
    try {
      const data = await obtenerStockBuckets(t.id);
      setBuckets(data);
    } catch {
      setBuckets([]);
    }
  };

  const bucketKey = (b: StockBucket) =>
    `${b.categoria || ""}|${b.identificador || ""}`;

  const toggleBucket = (b: StockBucket) => {
    setBucketsSel((prev) =>
      prev.some((x) => bucketKey(x) === bucketKey(b))
        ? prev.filter((x) => bucketKey(x) !== bucketKey(b))
        : [...prev, b]
    );
  };

  useEffect(() => {
    obtenerMisTemas()
      .then((lista) => {
        // En esta vista solo operan quienes pueden entregar, y solo temas de
        // entrega directa (los de consumo/instalacion se operan desde Mi stock).
        const operables = lista.filter(
          (t) => (t.es_admin || t.es_entregador) && t.modo_salida !== "consumo"
        );
        setTemas(operables);
        setTema(operables[0] || null);
      })
      .catch(() => {
        setTemas([]);
        setTema(null);
      })
      .finally(() => setCargandoTema(false));
    return () => {
      detenerScanner();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Al cambiar el tema seleccionado, refrescamos saldo e items.
  useEffect(() => {
    if (!tema) return;
    setBucketsSel([]);
    setBucketSearch("");
    refrescarSaldo(tema.id);
    cargarBuckets(tema);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tema?.id]);

  // ---------------------------------------------------------------------------
  // QR
  // ---------------------------------------------------------------------------
  const detenerScanner = async () => {
    try {
      if (scannerRef.current) {
        const s = scannerRef.current;
        scannerRef.current = null;
        // @ts-ignore html5-qrcode expone getState() en runtime
        if (s.getState && s.getState() === 2) {
          await s.stop();
        }
        await s.clear();
      }
    } catch {
      /* no-op */
    }
  };

  const procesarQR = async (qr: string) => {
    if (procesandoRef.current) return;
    const ahora = Date.now();
    if (qr === ultimoQRRef.current && ahora - ultimoQRTsRef.current < 3000) {
      return;
    }
    ultimoQRRef.current = qr;
    ultimoQRTsRef.current = ahora;
    procesandoRef.current = true;
    setEstadoQR("procesando");

    try {
      const ubicacion = await obtenerUbicacion();
      const resp = await escanearQR({
        qr,
        lat: ubicacion.lat,
        lng: ubicacion.lng,
        precision: ubicacion.precision,
      });
      setUltimo({
        receptor: resp.receptor?.nombre || resp.receptor?.cedula,
        mensaje: resp.mensaje,
      });
      if (typeof resp.saldo === "number") setSaldo(resp.saldo);
      setEstadoQR("ok");
      present({
        message: resp.mensaje || "Entrega registrada",
        duration: 3500,
        color: "success",
      });
    } catch (e: any) {
      const msg = e?.response?.data?.error || "No se pudo registrar la entrega";
      const previa = e?.response?.data?.entrega_previa;
      const receptor = e?.response?.data?.receptor;
      const nuevoSaldo = e?.response?.data?.saldo;
      if (typeof nuevoSaldo === "number") setSaldo(nuevoSaldo);
      setUltimo({
        receptor: receptor?.nombre || receptor?.cedula,
        mensaje: msg,
        detalle: previa?.fecha_hora
          ? `Ya retiro el ${new Date(previa.fecha_hora).toLocaleString()}`
          : undefined,
      });
      setEstadoQR("error");
      present({ message: msg, duration: 4000, color: "danger" });
    } finally {
      setTimeout(() => {
        procesandoRef.current = false;
      }, 1500);
    }
  };

  const iniciarCamara = async () => {
    setErrorQR("");
    setUltimo(null);
    setEstadoQR("iniciando");
    try {
      await detenerScanner();
      const scanner = new Html5Qrcode(SCANNER_ID, /* verbose */ false);
      scannerRef.current = scanner;
      await scanner.start(
        { facingMode: "environment" },
        { fps: 10, qrbox: { width: 250, height: 250 } },
        (decodedText) => procesarQR(decodedText),
        () => {
          /* errores de frame no nos interesan */
        }
      );
      setEstadoQR("escaneando");
    } catch (e: any) {
      setEstadoQR("error");
      const msg =
        typeof e === "string"
          ? e
          : e?.message || "No se pudo iniciar la camara. Verifica permisos.";
      setErrorQR(msg);
    }
  };

  const continuarEscaneo = () => {
    setEstadoQR("escaneando");
    setUltimo(null);
  };

  const irAQR = async () => {
    setModo("qr");
    setUltimo(null);
    setErrorQR("");
    // Inicio diferido: el div del scanner debe estar montado antes de iniciar.
    setTimeout(() => {
      iniciarCamara();
    }, 100);
  };

  // ---------------------------------------------------------------------------
  // Manual
  // ---------------------------------------------------------------------------
  const irAManual = async () => {
    await detenerScanner();
    setModo("manual");
    setEstadoManual("buscar");
    setBusqueda("");
    setResultados([]);
    setSeleccionado(null);
    setMotivo("");
    setResultadoManual(null);
  };

  const buscar = async () => {
    const q = busqueda.trim();
    if (!q) {
      setResultados([]);
      return;
    }
    setBuscando(true);
    try {
      const data = await buscarFuncionarios(q);
      setResultados(data.slice(0, 30));
    } catch (e: any) {
      present({
        message: e?.response?.data?.error || "Error al buscar funcionarios",
        duration: 3500,
        color: "danger",
      });
    } finally {
      setBuscando(false);
    }
  };

  const seleccionar = (f: FuncionarioActivo) => {
    setSeleccionado(f);
    setMotivo("");
    setEstadoManual("motivo");
  };

  const confirmarManual = async () => {
    if (!seleccionado) return;
    const m = motivo.trim();
    if (m.length < 3) {
      present({
        message: "Indica un motivo (minimo 3 caracteres)",
        duration: 2500,
        color: "warning",
      });
      return;
    }
    // En temas con categorias se entrega un item por cada serie seleccionada.
    // Sin seleccion (o tema general) se registra una entrega simple.
    const items: (StockBucket | null)[] =
      tema?.controla_categorias && bucketsSel.length ? bucketsSel : [null];
    const receptorNombre = seleccionado.nombre || seleccionado.cedula;
    setEstadoManual("enviando");
    try {
      const ubicacion = await obtenerUbicacion();
      let ok = 0;
      const errores: string[] = [];
      let ultimoSaldo: number | undefined;
      for (const b of items) {
        try {
          const resp = await entregarManual({
            cedula: seleccionado.cedula,
            motivo: m,
            lat: ubicacion.lat,
            lng: ubicacion.lng,
            precision: ubicacion.precision,
            tema_id: tema?.id,
            categoria: b?.categoria ?? undefined,
            identificador: b?.identificador ?? undefined,
          });
          ok++;
          if (typeof resp.saldo === "number") ultimoSaldo = resp.saldo;
        } catch (e: any) {
          const etiqueta = b ? bucketLabel(b) : "entrega";
          errores.push(`${etiqueta}: ${e?.response?.data?.error || "error"}`);
        }
      }
      if (typeof ultimoSaldo === "number") setSaldo(ultimoSaldo);
      if (tema) cargarBuckets(tema);
      setBucketsSel([]);

      if (ok > 0 && errores.length === 0) {
        setResultadoManual({
          receptor: receptorNombre,
          mensaje: items.length > 1 ? `${ok} items entregados` : "Entrega registrada",
        });
        setEstadoManual("ok");
        present({ message: `${ok} entrega(s) registrada(s)`, duration: 3000, color: "success" });
      } else if (ok > 0) {
        setResultadoManual({
          receptor: receptorNombre,
          mensaje: `${ok} entregado(s), ${errores.length} con error`,
          detalle: errores.join(" · "),
        });
        setEstadoManual("ok");
        present({ message: `Parcial: ${ok} ok, ${errores.length} con error`, duration: 4500, color: "warning" });
      } else {
        setResultadoManual({
          receptor: receptorNombre,
          mensaje: "No se pudo registrar la entrega",
          detalle: errores.join(" · "),
        });
        setEstadoManual("error");
        present({ message: errores[0] || "No se pudo registrar", duration: 4000, color: "danger" });
      }
    } catch (e: any) {
      setResultadoManual({
        receptor: receptorNombre,
        mensaje: e?.response?.data?.error || "No se pudo registrar la entrega manual",
      });
      setEstadoManual("error");
      present({ message: "No se pudo registrar la entrega", duration: 4000, color: "danger" });
    }
  };

  const otraManual = () => {
    setEstadoManual("buscar");
    setBusqueda("");
    setResultados([]);
    setSeleccionado(null);
    setMotivo("");
    setResultadoManual(null);
  };

  const volverMenu = async () => {
    await detenerScanner();
    setModo("menu");
    setUltimo(null);
    setErrorQR("");
    setResultadoManual(null);
    setSeleccionado(null);
    setMotivo("");
    setBusqueda("");
    setResultados([]);
  };

  // ---------------------------------------------------------------------------
  // Render
  // ---------------------------------------------------------------------------
  const titulo = tema?.nombre ? `Entrega - ${tema.nombre}` : "Registrar entrega";

  return (
    <IonPage>
      <IonHeader>
        <IonToolbar>
          <IonButtons slot="start">
            {modo === "menu" ? (
              <IonBackButton defaultHref="/home" />
            ) : (
              <IonButton onClick={volverMenu}>
                <IonIcon slot="icon-only" icon={arrowBackOutline} />
              </IonButton>
            )}
          </IonButtons>
          <IonTitle>{titulo}</IonTitle>
        </IonToolbar>
      </IonHeader>

      <IonContent className="ion-padding">
        {!cargandoTema && tema && saldo !== null && (
          <div
            style={{
              padding: "8px 12px",
              background: saldo > 0 ? "#e8f5e9" : "#ffebee",
              color: saldo > 0 ? "#1b5e20" : "#b71c1c",
              borderRadius: 8,
              marginBottom: 12,
              textAlign: "center",
              fontWeight: 600,
            }}
          >
            Tu saldo disponible: {saldo} {tema.descripcion || "kits"}
          </div>
        )}

        {cargandoTema && (
          <div style={{ textAlign: "center", padding: 24 }}>
            <IonSpinner name="crescent" />
          </div>
        )}

        {!cargandoTema && !tema && (
          <div style={{ textAlign: "center", padding: 24, color: "#777" }}>
            <IonIcon icon={alertCircleOutline} style={{ fontSize: 64 }} />
            <h3>Sin entregas activas</h3>
            <p>No hay un tema vigente en este momento.</p>
          </div>
        )}

        {/* ============================ MENU ================================ */}
        {!cargandoTema && tema && modo === "menu" && (
          <div style={{ paddingTop: 16 }}>
            {temas.length > 1 && (
              <div style={{ marginBottom: 12 }}>
                <div style={{ fontSize: 12, color: "#888", marginBottom: 4 }}>
                  Tema
                </div>
                <IonSelect
                  value={tema.id}
                  interface="action-sheet"
                  onIonChange={(e) => {
                    const elegido = temas.find((t) => t.id === e.detail.value);
                    if (elegido) setTema(elegido);
                  }}
                  style={{ border: "1px solid #ccc", borderRadius: 8, paddingLeft: 12 }}
                >
                  {temas.map((t) => (
                    <IonSelectOption key={t.id} value={t.id}>
                      {t.nombre}
                    </IonSelectOption>
                  ))}
                </IonSelect>
              </div>
            )}

            {tema.descripcion && (
              <p style={{ color: "#555", textAlign: "center" }}>
                {tema.descripcion}
              </p>
            )}

            {tema.controla_categorias && (
              <p style={{ color: "var(--garnet, #910434)", fontSize: 13, textAlign: "center", margin: "0 0 8px" }}>
                Para entregar items con N° de serie, usa "Registrar
                manualmente": vas a poder buscarlos y elegir varios.
              </p>
            )}

            <p style={{ textAlign: "center" }}>
              Como vas a registrar la entrega?
            </p>

            <IonButton
              expand="block"
              size="large"
              onClick={irAQR}
              style={{ marginTop: 16 }}
            >
              <IonIcon slot="start" icon={cameraOutline} />
              Escanear QR
            </IonButton>

            <IonButton
              expand="block"
              size="large"
              fill="outline"
              onClick={irAManual}
              style={{ marginTop: 12 }}
            >
              <IonIcon slot="start" icon={createOutline} />
              Registrar manualmente
            </IonButton>

            <p style={{ color: "#888", fontSize: 13, marginTop: 16, textAlign: "center" }}>
              Usa el registro manual solo cuando el QR no funcione (sin internet
              en el celu del colaborador, problema de camara, etc.).
            </p>
          </div>
        )}

        {/* ============================ QR ================================== */}
        {modo === "qr" && (
          <>
            <div
              id={SCANNER_ID}
              style={{
                width: "100%",
                maxWidth: 480,
                minHeight:
                  estadoQR === "iniciando" ||
                  estadoQR === "escaneando" ||
                  estadoQR === "procesando"
                    ? 280
                    : 0,
                margin: "0 auto",
                background: "#000",
                display:
                  estadoQR === "iniciando" ||
                  estadoQR === "escaneando" ||
                  estadoQR === "procesando"
                    ? "block"
                    : "none",
              }}
            />

            {estadoQR === "iniciando" && (
              <div style={{ textAlign: "center", padding: 16 }}>
                <IonSpinner name="crescent" />
                <div style={{ marginTop: 8 }}>Iniciando camara...</div>
              </div>
            )}

            {estadoQR === "procesando" && (
              <div style={{ textAlign: "center", marginTop: 16 }}>
                <IonSpinner name="dots" /> Registrando...
              </div>
            )}

            {estadoQR === "ok" && ultimo && (
              <div style={{ textAlign: "center", padding: 24 }}>
                <IonIcon
                  icon={checkmarkCircleOutline}
                  style={{ fontSize: 80, color: "#2e7d32" }}
                />
                <h3>Entrega registrada</h3>
                <p>
                  <b>{ultimo.receptor}</b>
                </p>
                <IonButton onClick={continuarEscaneo}>Escanear otro</IonButton>
              </div>
            )}

            {estadoQR === "error" && ultimo && (
              <div style={{ textAlign: "center", padding: 24 }}>
                <IonIcon
                  icon={alertCircleOutline}
                  style={{ fontSize: 80, color: "#c62828" }}
                />
                <h3>{ultimo.mensaje}</h3>
                {ultimo.receptor && (
                  <p>
                    Colaborador: <b>{ultimo.receptor}</b>
                  </p>
                )}
                {ultimo.detalle && (
                  <p style={{ color: "#666" }}>{ultimo.detalle}</p>
                )}
                <IonButton onClick={continuarEscaneo}>Escanear otro</IonButton>
              </div>
            )}

            {estadoQR === "error" && !ultimo && errorQR && (
              <div style={{ textAlign: "center", padding: 24, color: "#c62828" }}>
                {errorQR}
                <div style={{ marginTop: 16 }}>
                  <IonButton onClick={iniciarCamara}>Reintentar</IonButton>
                </div>
              </div>
            )}
          </>
        )}

        {/* ============================ MANUAL ============================== */}
        {modo === "manual" && estadoManual === "buscar" && (
          <>
            <h3 style={{ marginTop: 0 }}>Buscar colaborador</h3>
            <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
              <IonInput
                placeholder="Cedula o nombre"
                value={busqueda}
                onIonChange={(e) => setBusqueda(e.detail.value || "")}
                onKeyUp={(e) => {
                  if ((e as any).key === "Enter") buscar();
                }}
                style={{
                  border: "1px solid #ccc",
                  borderRadius: 4,
                  paddingLeft: 8,
                }}
              />
              <IonButton onClick={buscar} disabled={buscando}>
                <IonIcon slot="icon-only" icon={searchOutline} />
              </IonButton>
            </div>

            {buscando && <IonSpinner name="dots" />}
            <IonList>
              {resultados.map((f) => (
                <IonItem key={f.cedula} button onClick={() => seleccionar(f)}>
                  <IonLabel>
                    <h4>{f.nombre}</h4>
                    <p>
                      {f.cedula} - {f.cargo}
                    </p>
                  </IonLabel>
                  <IonButton slot="end" fill="solid">
                    Elegir
                  </IonButton>
                </IonItem>
              ))}
            </IonList>
          </>
        )}

        {modo === "manual" && estadoManual === "motivo" && seleccionado && (
          <>
            <h3 style={{ marginTop: 0 }}>Confirmar entrega manual</h3>
            <IonItem lines="full">
              <IonLabel>
                <h4>{seleccionado.nombre}</h4>
                <p>
                  {seleccionado.cedula} - {seleccionado.cargo}
                </p>
              </IonLabel>
            </IonItem>

            {tema?.controla_categorias && (
              <div style={{ marginTop: 12 }}>
                <div style={{ fontSize: 13, color: "#555", marginBottom: 6 }}>
                  Items a entregar{" "}
                  {bucketsSel.length > 0 && (
                    <b style={{ color: "var(--garnet, #910434)" }}>
                      ({bucketsSel.length} seleccionado(s))
                    </b>
                  )}
                </div>
                <IonItem>
                  <IonIcon icon={searchOutline} slot="start" />
                  <IonInput
                    placeholder="Buscar por categoria o N° de serie"
                    value={bucketSearch}
                    onIonChange={(e) => setBucketSearch(e.detail.value || "")}
                  />
                </IonItem>
                {buckets.length === 0 && (
                  <p style={{ color: "#999", fontSize: 13 }}>
                    No tenes items con saldo en tu stock.
                  </p>
                )}
                <IonList style={{ maxHeight: 260, overflowY: "auto" }}>
                  {buckets
                    .filter((b) => {
                      const q = bucketSearch.trim().toLowerCase();
                      if (!q) return true;
                      return (
                        (b.categoria || "").toLowerCase().includes(q) ||
                        (b.identificador || "").toLowerCase().includes(q)
                      );
                    })
                    .map((b) => {
                      const checked = bucketsSel.some(
                        (x) => bucketKey(x) === bucketKey(b)
                      );
                      return (
                        <IonItem key={bucketKey(b)} button onClick={() => toggleBucket(b)}>
                          <IonCheckbox slot="start" checked={checked} />
                          <IonLabel className="ion-text-wrap">
                            {bucketLabel(b)}
                            <span style={{ color: "#888" }}> · {b.saldo}</span>
                          </IonLabel>
                        </IonItem>
                      );
                    })}
                </IonList>
                <p style={{ color: "#999", fontSize: 12, margin: "4px 0 0" }}>
                  Si no seleccionas ninguno, se registra una entrega sin item.
                </p>
              </div>
            )}

            <IonItem lines="none" style={{ marginTop: 12 }}>
              <IonLabel position="stacked">
                Motivo (obligatorio)
              </IonLabel>
              <IonTextarea
                placeholder="Ej: el QR no se generaba, sin saldo, problema con la camara, etc."
                value={motivo}
                onIonChange={(e) => setMotivo(e.detail.value || "")}
                autoGrow
                rows={3}
              />
            </IonItem>

            <div style={{ display: "flex", gap: 8, marginTop: 16 }}>
              <IonButton
                expand="block"
                fill="outline"
                onClick={() => setEstadoManual("buscar")}
                style={{ flex: 1 }}
              >
                Cambiar persona
              </IonButton>
              <IonButton
                expand="block"
                onClick={confirmarManual}
                style={{ flex: 1 }}
              >
                Confirmar entrega
              </IonButton>
            </div>
          </>
        )}

        {modo === "manual" && estadoManual === "enviando" && (
          <div style={{ textAlign: "center", padding: 24 }}>
            <IonSpinner name="crescent" />
            <div style={{ marginTop: 8 }}>Registrando...</div>
          </div>
        )}

        {modo === "manual" && estadoManual === "ok" && resultadoManual && (
          <div style={{ textAlign: "center", padding: 24 }}>
            <IonIcon
              icon={checkmarkCircleOutline}
              style={{ fontSize: 80, color: "#2e7d32" }}
            />
            <h3>Entrega registrada</h3>
            <p>
              <b>{resultadoManual.receptor}</b>
            </p>
            <IonButton onClick={otraManual}>Registrar otra</IonButton>
          </div>
        )}

        {modo === "manual" && estadoManual === "error" && resultadoManual && (
          <div style={{ textAlign: "center", padding: 24 }}>
            <IonIcon
              icon={alertCircleOutline}
              style={{ fontSize: 80, color: "#c62828" }}
            />
            <h3>{resultadoManual.mensaje}</h3>
            {resultadoManual.receptor && (
              <p>
                Colaborador: <b>{resultadoManual.receptor}</b>
              </p>
            )}
            {resultadoManual.detalle && (
              <p style={{ color: "#666" }}>{resultadoManual.detalle}</p>
            )}
            <div style={{ display: "flex", gap: 8, justifyContent: "center" }}>
              <IonButton fill="outline" onClick={() => setEstadoManual("motivo")}>
                Reintentar
              </IonButton>
              <IonButton onClick={otraManual}>Registrar otra</IonButton>
            </div>
          </div>
        )}
      </IonContent>
    </IonPage>
  );
};

export default RegistrarEntrega;
